import { useEffect } from "react";
import { motion } from "framer-motion";
import { Flame, ChefHat, Utensils, Truck, ConciergeBell, Warehouse, Factory, ShoppingBag, Coffee, MessageCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { useQuote } from "./Header";
import { SITE } from "../../lib/config";
import { track } from "../../lib/analytics";

const APPLICATIONS = [
  { icon: Utensils, t: "Restaurants" },
  { icon: ChefHat, t: "Cloud Kitchens" },
  { icon: ConciergeBell, t: "Caterers & Events" },
  { icon: Truck, t: "Food Delivery Brands" },
  { icon: Flame, t: "Bakeries & Sweet Shops" },
  { icon: Coffee, t: "Cafes & QSRs" },
  { icon: Factory, t: "Food Manufacturers" },
  { icon: ShoppingBag, t: "Cloud Kitchens & Takeaways" },
  { icon: Warehouse, t: "Wholesale Distributors" },
];

const SIZES = [
  { code: "83", label: "Container No. 83", short: "No. 83", cap: "~250 ml", desc: "Small aluminium foil container for chutneys, side portions and single-serve gravies.", uses: "Chutneys • Sides" },
  { code: "204", label: "Container No. 204", short: "No. 204", cap: "~450 ml", desc: "Compact foil container for single-meal curries, rice sides and biryani portions.", uses: "Curry • Rice sides" },
  { code: "450", label: "Container No. 450", short: "No. 450", cap: "~450 ml", desc: "Popular takeaway aluminium foil container size for restaurants and cloud kitchens.", uses: "Meal portions" },
  { code: "660", label: "Container No. 660", short: "No. 660", cap: "~660 ml", desc: "Mid-size foil container for biryani, rice and single-serve meals.", uses: "Biryani • Meals" },
  { code: "750", label: "Container No. 750", short: "No. 750", cap: "~750 ml", desc: "Popular biryani foil container for cloud kitchens, caterers and food delivery.", uses: "Biryani • Combos" },
  { code: "830", label: "Container No. 830", short: "No. 830", cap: "~830 ml", desc: "Larger foil container for family meals, catering orders and party packs.", uses: "Family meals • Catering" },
];

const TYPES = [
  "Silver aluminium foil containers",
  "Wrinkle-free foil containers",
  "Round foil containers",
  "Rectangle foil containers",
  "Foil containers with board lids",
  "Foil containers with foil lids",
];

const FAQS = [
  { q: "What are aluminium foil containers used for?",
    a: "Aluminium foil containers are used by restaurants, cloud kitchens, caterers, bakeries and food delivery businesses to pack biryani, curries, rice, meals, baked items and other hot foods for takeaway and delivery." },
  { q: "Do you supply aluminium foil containers in wholesale quantities?",
    a: "Yes. Chennai Packs supplies aluminium foil containers in wholesale/bulk quantities to restaurants, cloud kitchens, caterers, bakeries and food delivery brands. Share your required container number and quantity for pricing." },
  { q: "What sizes / container numbers are available?",
    a: "Our aluminium foil container range includes popular container numbers 83, 204, 450, 660, 750 and 830 — suitable for chutneys, sides, single-serve meals, biryani and family portions. Enquire with your required container number for availability." },
  { q: "Are aluminium foil containers oven-safe?",
    a: "Yes. Aluminium foil containers are oven-safe and ideal for baking, reheating and hot-food takeaway. Avoid microwave use." },
  { q: "Do foil containers come with lids?",
    a: "Yes. Board lids and foil lids are available for our aluminium foil containers. Confirm the required lid type when requesting a quote." },
  { q: "Are the foil containers suitable for biryani takeaway?",
    a: "Yes. Foil containers (especially No. 660, 750 and 830) are widely used by biryani shops, cloud kitchens and caterers for takeaway biryani, family meals and combo packs." },
  { q: "Do you supply aluminium foil containers in Chennai?",
    a: "Yes. Chennai Packs supplies aluminium foil containers to restaurants, caterers, cloud kitchens and food delivery brands across Chennai, Tamil Nadu and pan-India." },
  { q: "How can I get a wholesale quote?",
    a: "Click ‘Get a Quote’ or ‘Enquire on WhatsApp’ on this page, share your required container number and quantity, and our team will respond with wholesale pricing." },
];

export default function FoilContainersContent() {
  const openQuote = useQuote();
  const waHref = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hi Chennai Packs, I'd like a quote for Aluminium Foil Containers.")}`;

  useEffect(() => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-foil-faq", "1");
    s.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question", name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(s);
    return () => {
      document.head.querySelectorAll('script[data-foil-faq="1"]').forEach((n) => n.remove());
    };
  }, []);

  return (
    <>
      <section className="py-16" data-testid="foil-intro">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Aluminium Foil Containers for Biryani, Curries & Takeaway
          </h2>
          <p className="text-lg text-[#3A4D3E] mt-4 leading-relaxed">
            Chennai Packs supplies oven-safe aluminium foil containers for restaurants, cloud
            kitchens, caterers, bakeries and food delivery brands. Available in popular container
            numbers 83, 204, 450, 660, 750 and 830 — with board and foil lid options.
          </p>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAF8]" data-testid="foil-sizes">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Aluminium Foil Container Sizes
          </h2>
          <p className="text-[#3A4D3E] mt-3 max-w-2xl">
            Our aluminium foil containers are stocked in the most-used trade numbers. Tap a
            container number to WhatsApp us directly with your requirement.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SIZES.map((s) => {
              const waMsg = `Hi Chennai Packs, I'd like a quote for Aluminium Foil Containers — ${s.short} (${s.cap}). Please share pricing and MOQ. Thank you.`;
              const waUrl = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(waMsg)}`;
              return (
                <article
                  key={s.code}
                  className="bg-white border border-[#E2E8E4] rounded-2xl p-6 hover:-translate-y-1 hover:shadow-xl transition-all duration-500"
                  data-testid={`foil-size-${s.code}`}
                >
                  <span className="text-[11px] font-bold uppercase tracking-widest text-[#1D6B2A]">
                    Container
                  </span>
                  <h3 className="font-head text-2xl font-semibold text-[#0A1C0E] mt-1">
                    {s.label}
                  </h3>
                  <p className="text-xs text-[#3A4D3E] mt-1 font-medium">Approx capacity: {s.cap}</p>
                  <p className="text-sm text-[#3A4D3E] mt-3 leading-relaxed">{s.desc}</p>
                  <p className="text-xs text-[#0A1C0E] mt-3 font-medium">{s.uses}</p>
                  <div className="mt-5 flex flex-col gap-2">
                    <a
                      href={waUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => track("Foil Size WhatsApp", { size: s.code })}
                      data-testid={`foil-wa-${s.code}`}
                      className="inline-flex items-center justify-center gap-2 border border-[#1D6B2A] text-[#1D6B2A] rounded-full px-4 py-2 text-xs font-semibold hover:bg-[#F8FAF8] transition-colors"
                    >
                      <MessageCircle size={14} /> WhatsApp for {s.short}
                    </a>
                    <button
                      onClick={() => openQuote({ name: `Aluminium Foil Containers — ${s.short}`, sku: "CP-016" })}
                      data-testid={`foil-quote-${s.code}`}
                      className="bg-[#1D6B2A] text-white rounded-full px-4 py-2 text-xs font-semibold hover:bg-[#154d1e] transition-colors"
                    >
                      Get a Quote for {s.short}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16" data-testid="foil-applications">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Ideal for Restaurants, Cloud Kitchens & Caterers
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {APPLICATIONS.map((a) => (
              <div key={a.t} className="bg-white border border-[#E2E8E4] rounded-2xl p-5 flex items-center gap-4">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#F8FAF8] text-[#1D6B2A] shrink-0">
                  <a.icon size={22} />
                </span>
                <span className="font-head font-semibold text-[#0A1C0E]">{a.t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAF8]" data-testid="foil-types">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Aluminium Foil Container Options
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {TYPES.map((t) => (
              <div key={t} className="bg-white border border-[#E2E8E4] rounded-xl px-5 py-4 text-[#0A1C0E]">
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16" data-testid="foil-wholesale">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Wholesale Aluminium Foil Containers in Chennai
          </h2>
          <p className="text-lg text-[#3A4D3E] mt-4 leading-relaxed">
            Chennai Packs supplies aluminium foil containers to restaurants, cloud kitchens,
            caterers, bakeries and food delivery brands in Chennai. Enquire with your required
            container number and quantity for wholesale pricing.
          </p>
          <div className="mt-6 flex flex-wrap gap-4">
            <button
              onClick={() => openQuote({ name: "Aluminium Foil Containers", sku: "CP-016" })}
              data-testid="foil-wholesale-quote"
              className="bg-[#1D6B2A] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#154d1e] transition-colors"
            >
              Get a Quote
            </button>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="border border-[#1D6B2A] text-[#1D6B2A] rounded-full px-8 py-4 font-semibold hover:bg-[#F8FAF8] transition-colors"
            >
              Enquire on WhatsApp
            </a>
          </div>
        </div>
      </section>

      <section className="py-16" data-testid="foil-faq">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <h2 className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-3 mt-8">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`foil-${i}`} className="bg-white border border-[#E2E8E4] rounded-2xl px-6 data-[state=open]:shadow-md transition-shadow">
                <AccordionTrigger className="font-head text-base font-semibold text-[#0A1C0E] hover:no-underline text-left py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#3A4D3E] leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-12 bg-[#F8FAF8]" data-testid="foil-related">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <h3 className="font-head text-xl font-semibold text-[#0A1C0E]">Related packaging you may need</h3>
          <div className="mt-4 flex flex-wrap gap-3">
            {[
              { to: "/products/pp-biriyani-containers", label: "PP Food Containers" },
              { to: "/products/paper-meal-box-containers", label: "Paper Meal Boxes" },
              { to: "/products/aluminium-foil", label: "Aluminium Foil Rolls" },
              { to: "/products/cling-film", label: "Cling Film" },
              { to: "/products?category=aluminium-films", label: "Aluminium & Films" },
            ].map((l) => (
              <a key={l.to} href={l.to} className="border border-[#E2E8E4] bg-white rounded-full px-5 py-2 text-sm font-medium text-[#1D6B2A] hover:bg-[#F0F4F0]">
                {l.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16" data-testid="foil-final-cta">
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-head text-3xl md:text-4xl font-medium tracking-tight text-[#0A1C0E]"
          >
            Looking for Aluminium Foil Containers in Chennai?
          </motion.h2>
          <p className="text-lg text-[#3A4D3E] mt-4 max-w-2xl mx-auto">
            Tell us your required container number and quantity. Enquire with Chennai Packs for
            availability and wholesale pricing.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => openQuote({ name: "Aluminium Foil Containers", sku: "CP-016" })}
              data-testid="foil-final-quote"
              className="bg-[#1D6B2A] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#154d1e] transition-colors"
            >
              Get a Quote
            </button>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="border border-[#1D6B2A] text-[#1D6B2A] rounded-full px-8 py-4 font-semibold hover:bg-[#F8FAF8] transition-colors"
            >
              Enquire on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
